import React, { useEffect, useState } from 'react';
import { CheckCircle2, Star, Clock, Route, Coffee, Power } from 'lucide-react';
import { formatMWK, formatHm } from './tokens';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Screen 6 — Trip completion card.
 * Slides up after the trip ends. Shows earnings, performance chips and
 * today's running total + goal progress. Two CTAs: keep earning or break.
 *
 * Props:
 *   - completion: { ride, charges }  (output of /api/rides/{id}/complete)
 *   - driverId
 *   - onBackOnline: () => void
 *   - onBreak:     () => void
 */
const TripCompletionCard = ({ completion, driverId, onBackOnline, onBreak }) => {
  const ride = completion?.ride || {};
  const charges = completion?.charges || {};
  const earnings = charges.driver_earnings ?? ride.driver_earnings ?? ride.final_price ?? ride.estimated_price ?? 0;
  const tip = ride.tip_amount || 0;
  const waitFee = ride.wait_fee || 0;
  const base = Math.max(0, earnings - tip - waitFee);

  const [today, setToday] = useState(null);

  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;
    fetch(`${API}/api/driver/v2/${driverId}/today-metrics`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d?.success) setToday(d); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [driverId]);

  // Performance chips — derived; can later read from real ride fields.
  const rated = Number(ride.driver_rating || 5);
  const onTime = ride.pickup_was_on_time !== false;
  const efficient = ride.route_was_efficient !== false;

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-[2px] flex items-end justify-center" data-testid="trip-completion-overlay">
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl shadow-[0_-12px_40px_rgba(0,0,0,0.25)] animate-slide-up overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}
      >
        {/* Header celebration */}
        <div className="relative bg-gradient-to-br from-emerald-50 via-emerald-50 to-white pt-6 pb-4 px-5 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center mb-2 ring-4 ring-emerald-100">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={2.5} />
          </div>
          <p className="text-[12px] uppercase tracking-wider font-semibold text-emerald-700">Trip complete!</p>
          <p className="text-[34px] font-bold text-gray-900 mt-1 tabular-nums" data-testid="trip-completion-earned">
            {formatMWK(earnings)} earned
          </p>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Base {formatMWK(base)}
            {waitFee > 0 ? ` · Wait ${formatMWK(waitFee)}` : ''}
            {tip > 0 ? ` · Tip ${formatMWK(tip)}` : ''}
          </p>
        </div>

        {/* Performance chips */}
        <div className="px-5 pt-3 pb-2 grid grid-cols-3 gap-2">
          <PerfChip
            tone="amber"
            icon={<Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />}
            label={`Rated ${rated}★`}
            testid="trip-completion-rated"
          />
          <PerfChip
            tone={onTime ? 'emerald' : 'gray'}
            icon={<Clock className="w-3.5 h-3.5" />}
            label={onTime ? 'On-time pickup' : 'Late pickup'}
            testid="trip-completion-ontime"
          />
          <PerfChip
            tone={efficient ? 'emerald' : 'gray'}
            icon={<Route className="w-3.5 h-3.5" />}
            label={efficient ? 'Route efficient' : 'Detour'}
            testid="trip-completion-route"
          />
        </div>

        {/* Today's running total + goal */}
        {today && (
          <div className="mx-5 my-3 bg-gray-50 rounded-2xl p-4" data-testid="trip-completion-today-block">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Today so far</p>
            <p className="text-[18px] font-bold text-gray-900 mt-0.5 tabular-nums">
              {formatMWK(today.earned_today)} <span className="text-[12px] text-gray-500 font-medium">· {today.trips_today} trips · {formatHm(today.online_seconds)}</span>
            </p>
            {today.daily_goal > 0 && (
              <>
                <p className="text-[11px] text-gray-600 mt-2 mb-1.5 tabular-nums">
                  Goal {formatMWK(today.daily_goal)} · {Math.round(today.progress_pct)}% complete
                </p>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-[width] duration-700 ease-out"
                    style={{ width: `${Math.min(100, today.progress_pct || 0)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="px-4 pt-1 pb-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={onBackOnline}
            data-testid="trip-completion-back-online"
            className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[16px] font-bold tracking-wide shadow-lg shadow-emerald-600/30 active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2"
          >
            <Power className="w-4 h-4" strokeWidth={2.5} /> Go back online
          </button>
          <button
            type="button"
            onClick={onBreak}
            data-testid="trip-completion-break"
            className="w-full h-12 rounded-full bg-white text-gray-700 text-[14px] font-semibold border border-gray-200 hover:bg-gray-50 inline-flex items-center justify-center gap-2"
          >
            <Coffee className="w-4 h-4" /> Take a break
          </button>
        </div>
      </div>
    </div>
  );
};

const PerfChip = ({ tone = 'emerald', icon, label, testid }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <div
      className={`${tones[tone] || tones.gray} rounded-2xl py-2 px-2 inline-flex items-center justify-center gap-1`}
      data-testid={testid}
    >
      {icon}
      <span className="text-[11px] font-semibold tracking-tight truncate">{label}</span>
    </div>
  );
};

export default TripCompletionCard;
